import { useState } from "react";
import { Mic, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { listenOnce, scorePronunciation, speechSupported } from "@/lib/speech";
import { api } from "@/lib/api";
import { toast } from "sonner";

const ERRORS = {
  "not-allowed": "Autorisez le micro pour cet exercice.",
  "no-speech": "Je n'ai rien entendu. Réessayez plus fort.",
  unsupported: "Ce navigateur ne reconnaît pas la voix (essayez Chrome).",
};

export function PronounceButton({ word, testId, big = false, onResult }) {
  const [state, setState] = useState("idle");
  const [res, setRes] = useState(null);
  if (!speechSupported()) return big ? <p className="text-sm text-slate-600" data-testid={`${testId}-unsupported`}>{ERRORS.unsupported}</p> : null;

  const go = async () => {
    setState("listening");
    setRes(null);
    try {
      const heard = await listenOnce();
      const r = heard.length ? scorePronunciation(word, heard) : { heard: "", ok: false, error: "no-speech" };
      setRes(r);
      onResult?.(r);
      if (!r.error) {
        api.post("/pronunciation", { word, ok: r.ok, heard: r.heard || "" })
          .then(({ data }) => data.new_badge && toast.success("Nouveau badge : Bonne diction !", { description: `${data.words_ok} mots bien prononcés` }))
          .catch(() => {});
      }
    } catch (e) {
      setRes({ ok: false, heard: "", error: e.message });
    }
    setState("idle");
  };

  return (
    <div className={big ? "space-y-3" : "inline-flex flex-col items-start gap-1"}>
      <button
        onClick={go}
        disabled={state === "listening"}
        data-testid={testId}
        aria-label="Répéter au micro"
        className={`${big ? "w-24 h-24 mx-auto" : "w-12 h-12"} rounded-full grid place-items-center transition-colors ${
          state === "listening" ? "bg-red-600 text-white animate-pulse" : "bg-orange-500 hover:bg-orange-600 text-white"
        }`}
      >
        {state === "listening" ? <Loader2 className={`${big ? "w-10 h-10" : "w-6 h-6"} animate-spin`} /> : <Mic className={big ? "w-10 h-10" : "w-6 h-6"} />}
      </button>
      {res && (
        <div
          data-testid={`${testId}-result`}
          className={`rounded-xl px-3 py-2 text-sm font-semibold flex items-center gap-2 ${res.ok ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}
        >
          {res.ok ? <CheckCircle2 className="w-5 h-5 shrink-0" /> : <XCircle className="w-5 h-5 shrink-0" />}
          <span>
            {res.error ? ERRORS[res.error] || "Réessayez." : res.ok ? "Bien compris !" : "Pas encore."}
            {res.heard && <span className="block font-normal">Entendu : « {res.heard} »</span>}
          </span>
        </div>
      )}
    </div>
  );
}
